import ScreenContainer from 'common/components/screenContainer/ScreenContainer';
import ScreenHeader from 'common/components/screenHeader/ScreenHeader';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle'
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import styles from './StoppingAreasListScreen.module.scss';
import Box from 'common/components/box/Box';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { StoppingAreasListItemDto } from 'api/stoppingAreas/models/StoppingAreasListItemDto';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import { Col, Row } from 'react-flexbox-grid';
import Loading from 'common/services/Loading';
import StoppingAreasService from 'api/stoppingAreas/StoppingAreasService';
import { StoppingAreasSearchCriteria } from 'api/stoppingAreas/models/StoppingAreasSearchCriteria';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';

interface Filters {
    page?: number;
    allIn?: string
}

const StoppingAreasListScreen = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { addToast } = useToasts();

    const [stoppingAreas, setStoppingAreas] = useState<StoppingAreasListItemDto[]>([])
    const [totalItems, setTotalItems] = useState<number>(0);
    const [criteria, setCriteria] = useState<StoppingAreasSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn:'name',
        orderBy: 'asc'
    });

    useEffect(() => {
            loadFilters();
        }, [criteria]);

    const loadFilters = () => {      
        getData()
    }
    
    const getData = async () => {
        try {
            Loading.show();

            const [stoppingAreasItems] = await Promise.all([
                StoppingAreasService.getList(criteria),
            ]);

            if ((stoppingAreasItems?.totalPages ?? 0 > 0)  && (stoppingAreasItems.currentPage > (stoppingAreasItems?.totalPages ?? 0))) {
                criteria.page = 1;
                getData()
                return;
            }
            setStoppingAreas(stoppingAreasItems.items ?? []);
            setTotalItems(stoppingAreasItems.totalItems)
            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get stopping areas list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    }
   
    const navigateTo = (type?: string, id?: string) => {
        if (id) {
            history.push(`/stopping-area/${type}/${id}`);
        } else {
            history.push(`/stopping-area/${type}`);
        }
    }

    const onPageChange = (page: number) => {
        criteria.page = page;
        getData();
    }

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<StoppingAreasListItemDto>[] = useMemo(() => {
        const cols: ListingTableColumn<StoppingAreasListItemDto>[] = [     
            {
                name: t('stopping_areas.list.name'),
                renderCell: row => renderTableCell(row.name),
                onSearch: onTableFilter,
                searchField: 'name',
                width: 'fill'
            }
        ];
        return cols;
    }, [onTableFilter]);

    const renderTableCell = (text: string | number) => {
        return (
            <div>{text}</div>
        )
    }
   
    const onRowClick = (event: any, id?: string) => {
        if (id) {
            event.preventDefault();
            event.stopPropagation();

            if (event.ctrlKey) {
                window.open(history.location.pathname + '/details/' + id, '_blank');
                return;
            }
            navigateTo('details', id);
        }
    }
    
    const renderSearchInput = () => {
        return <InputSearch
            inputContainerClassName={styles.inputSearchContainerCustomization}
            onChangeValue={debounced}
            placeholder={t('stopping_areas.list.search_by_name')}
            defaultValue={criteria.allIn}
        ></InputSearch>
    }

    const debounced = useDebouncedCallback((value: string) => {
        setCriteria({ ...criteria, page: 1, allIn: value })
    }, 500);

    return (
        <ScreenTitle title={t('stopping_areas.list.title')}>
            <ScreenContainer>
                <ScreenHeader title={t('stopping_areas.list.title')}>
                    <Row>
                        <Col xs={12}>{renderSearchInput()}</Col>
                    </Row>                  
                    <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />
                </ScreenHeader>
                <Box className={styles.box}>
                    <ListingTable
                        columns={tableColumns}
                        rows={stoppingAreas}
                        onRowClick={(row, _, event) => onRowClick(event, row.id)}
                        allowHover={true}
                        onHref={row => `stopping-area/details/${row.id}`}
                        initialSearch={{ colField: 'name', isOrderAsc: true }}
                    />
                    <PaginationWithInfo
                        itemName={t('stopping_areas.list.title')}
                        currentPage={criteria.page}
                        pageItems={stoppingAreas.length}
                        totalItems={totalItems}
                        onChange={onPageChange}
                    />
                </Box>
            </ScreenContainer>
        </ScreenTitle>
    )
}

export default StoppingAreasListScreen